import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  Output,
  Renderer2,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NumberFormatMask } from '@macs-frontend/web-macs/shared/models/number-format-mask';
import { Store } from '@ngrx/store';
import IMask from 'imask';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getCurrentCompanyNumberMask } from '../../../reducers/meta';

// eslint-disable-next-line functional/no-let
let maskSubscription: Subscription;
// eslint-disable-next-line functional/no-let
let maskValue: NumberFormatMask;

@Directive({
  selector: '[mdNumberInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberInputDirective),
      multi: true,
    },
  ],
})
export class NumberInputDirective
  implements OnDestroy, ControlValueAccessor, AfterViewInit
{
  destroy$ = new Subject<void>();

  @Input() mdNumberInput: any;
  companyNumberMask$ = this.store$.select(getCurrentCompanyNumberMask);
  @Input() unmask?: boolean | 'typed' = 'typed';
  @Input() allowNegativeNumbers = true;
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() input: EventEmitter<Partial<Event>> = new EventEmitter<Partial<Event>>();

  private maskRef?: IMask.InputMask<any>;
  private onTouched: any = () => {};
  private onChange: any = () => {};

  /**
   * Value handed to writeValue() before the mask existed, waiting for
   * ngAfterViewInit. NOT null/undefined — those are legal "no value" writes
   * that writeValue ignores, so they cannot double as the empty marker.
   */
  private pendingValue?: { value: any };

  get element(): HTMLInputElement {
    return this.elementRef.nativeElement;
  }

  set maskValue(value: any) {
    if (this.maskRef) {
      if (this.unmask === 'typed') {
        // Set the masked value from a string that uses the mask's decimal
        // radix. IMask's `typedValue` internally re-stringifies numbers with a
        // JS "." and then reads that "." as the thousands separator (so 2433.3
        // became "24.333"). Swapping "." → radix first avoids that.
        if (value === null || value === undefined || value === '') {
          this.maskRef.value = '';
        } else {
          const radix = (maskValue && (maskValue as any).radix) || '.';
          let str = String(value);
          if (radix !== '.' && str.indexOf('.') !== -1) {
            str = str.replace('.', radix);
          }
          this.maskRef.value = str;
        }
      } else if (this.unmask) {
        this.maskRef.unmaskedValue = value;
      } else {
        this.maskRef.value = value;
      }
    } else {
      this.renderer.setProperty(this.element, 'value', value);
    }
  }

  get maskValue(): any {
    // eslint-disable-next-line functional/no-let
    let value: any = this.element.value;
    if (this.maskRef) {
      if (this.unmask === 'typed') {
        value = this.maskRef.typedValue;
      } else if (this.unmask) {
        value = this.maskRef.unmaskedValue;
      } else {
        value = this.maskRef.value;
      }
    }
    return value;
  }

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    readonly store$: Store<any>,
  ) {}

  ngAfterViewInit(): void {
    if (!maskSubscription) {
      maskSubscription = this.companyNumberMask$
        .pipe(takeUntil(this.destroy$))
        .subscribe(mask => (maskValue = mask));
    }

    const maskOptions = {
      ...maskValue,
      signed: this.allowNegativeNumbers,
    };

    this.maskRef = IMask(this.element, maskOptions);

    // Apply the value Angular wrote before the mask existed, now that the
    // radix-aware setter can format it. Done BEFORE the listeners are
    // attached so the initial formatting doesn't echo back into the form —
    // same as IMask's own reformatting of a pre-filled element.
    if (this.pendingValue) {
      const { value } = this.pendingValue;
      this.pendingValue = undefined;
      this.maskValue = value;
    }

    this.maskRef.on('accept', () => this._onAccept());
    this.maskRef.on('complete', () => this._onComplete());
  }

  ngOnDestroy(): void {
    if (this.maskRef) {
      this.maskRef.destroy();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('input', ['$event'])
  handleInput(event: { preventDefault: () => void }) {
    if (event.preventDefault) {
      event.preventDefault();
      // eslint-disable-next-line functional/no-let
      let value: string | number = '';
      setTimeout((_: any) => {
        if (!this.maskRef) {
          value = this.element.value;
        } else {
          if (this.unmask === 'typed') {
            value = this.maskRef.typedValue as string | number;
          } else if (this.unmask) {
            value = this.maskRef.unmaskedValue;
          }
        }

        this.input.emit({
          target: {
            ...this.elementRef.nativeElement,
            value,
          },
        });
      });
    }
  }

  writeValue(value: any): void {
    if (value === undefined || value === null) {
      return;
    }

    // Angular writes the control's value while binding inputs — before
    // ngAfterViewInit, so before the mask exists. Writing the raw JS string
    // straight to the DOM here handed IMask "65.5"; under a comma radix the
    // "." is neither the radix nor the thousands separator, so IMask dropped
    // it and the field read 655. Every form whose state is already populated
    // when the input renders (an estimate opened for edit, say) hit this on
    // the first write and showed decimals as whole numbers.
    if (!this.maskRef) {
      this.pendingValue = { value };
      return;
    }

    this.maskValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.element, 'disabled', isDisabled);
  }

  private _onAccept(): void {
    this.onChange(this.maskValue);
  }

  private _onComplete(): void {
    this.onTouched();
  }
}
